/**
 * Component for escalating the invoices to collections.
 * Triggered when clicking the `Action` button for collection type items selected.
 */

import { Button, Col, Row, Modal } from 'antd';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withDateFormatHandler } from '../../components/common/DateFormatHandler';
import ModalWithSpinner from '../../components/common/ModalWithSpinner';
import { isEmpty, map, capitalize } from 'lodash';
import { escalateToCollectionsAction } from '../../store/tasks/actions';
import { getUserCompanyCustomerWorkflowFlag } from '../../store/companies/sagas';

interface IProps {
    readonly visible: boolean;
    readonly closePanel?: (refetchList?: boolean) => void;
    readonly getSelectedTasksValues: () => {
        allExcept: boolean;
        keysToUse: string[];
        filterObject: any;
    };
}

const TaskEscalateCollectionsPanel: React.FC<IProps> = ({
    closePanel,
    getSelectedTasksValues,
}: IProps) => {
    const dispatch = useDispatch();

    const usingCustomerWorkflow: boolean = useSelector(
        getUserCompanyCustomerWorkflowFlag
    );

    const [submitLoading, setSubmitLoading] = useState<boolean>(false);

    /**
     * Function called to close the panel when `No` button is clicked.
     */
    const handleClosePanel = () => {
        if (closePanel) closePanel();
    };

    /**
     * Function called when `Yes` button is clicked.
     * Escalates the items selected to collections.
     */
    const handleSubmitForm = () => {
        const { allExcept, keysToUse, filterObject } = getSelectedTasksValues();

        setSubmitLoading(true);
        dispatch(
            escalateToCollectionsAction({
                filter: filterObject,
                taskIds: keysToUse,
                excludeTasks: allExcept,
                usingCustomerWorkflow,
                callback: escalateToCollectionResponseModal,
            })
        );
    };

    let itemText = 'invoice';
    if (usingCustomerWorkflow) {
        itemText = 'customer';
    }

    /**
     * Function called after calling the API for escalating to collections.
     * Either success/error.
     * @param param0
     */
    const escalateToCollectionResponseModal = ({
        IsSuccess,
        Messages,
    }: {
        IsSuccess: boolean;
        Messages: string[] | undefined;
    }) => {
        setSubmitLoading(false);

        if (IsSuccess) {
            Modal.success({
                title: 'Success',
                content: `${capitalize(
                    itemText
                )}(s) escalated to collections successfully!`,
                onOk: () => {
                    if (closePanel) closePanel(true);
                },
            });
        } else {
            let errorMessageContent:
                | string
                | JSX.Element[] = `Failed to escalate ${itemText}(s) to collection!`;
            if (!isEmpty(Messages)) {
                errorMessageContent = map(
                    Messages,
                    (error: string, index: number) => (
                        <div key={index}>{error}</div>
                    )
                );
            }

            Modal.error({
                title: 'Error',
                content: errorMessageContent,
            });
        }
    };

    /**
     * Function to populate the content (confirmation message) of this panel.
     */
    const populatePanelContent = () => {
        const { allExcept, keysToUse } = getSelectedTasksValues();
        let phraseToUse = 'these items';
        if (!allExcept && keysToUse.length === 1) {
            phraseToUse = 'this item';
        }
        return (
            <Row>
                <Col>
                    Do you want to escalate {phraseToUse} to your collection
                    team?
                </Col>
            </Row>
        );
    };

    return (
        <Row>
            <Col>
                <div>{populatePanelContent()}</div>
                <br />
                <Row>
                    <Col className="ta-right" span={24}>
                        <Button
                            className="mr-8"
                            type="primary"
                            onClick={handleSubmitForm}
                        >
                            Yes
                        </Button>
                        <Button onClick={handleClosePanel}>No</Button>
                    </Col>
                </Row>
            </Col>
            {submitLoading && (
                <ModalWithSpinner
                    modalTitle="Escalating to collection"
                    modalVisible={submitLoading}
                    displayMessage={`Please wait while escalating ${itemText}(s) to collections . . .`}
                />
            )}
        </Row>
    );
};

export default withRouter(withDateFormatHandler(TaskEscalateCollectionsPanel));
